import React, { useEffect, useRef } from 'react';
import { Id, Value } from './types';
import styles from './SelectInput.module.scss';
import classNames from 'classnames';
import Icon from '../Icon/Icon';

const ANIMATION_DURATION = 200;

interface IProps {
  id: string,
  current: Id
  values: Value[],
  expanded: boolean,
  onExpand: (expanded: boolean) => void,
  onSelect: (id: Id) => void,
}

const noop = () => {
};

const SelectInput: React.FC<IProps> = ({
                                         id,
                                         values = [], onSelect, current,
                                         expanded, onExpand
                                       }) => {
  const refList = useRef<HTMLUListElement>(null);
  const refListItem = useRef<HTMLLIElement>(null);
  const curr = values.find((val: Value) => val.id === current);
  const currLabel = curr?.label;
  const currUrl = curr?.iconUrl;
  const buttonRef = useRef<HTMLButtonElement>(null);
  const openSelectButtonRef = useRef<HTMLButtonElement>(null);

  const clickHandler = (id: Id) => {
    onExpand(false);
    onSelect(id);
  };

  useEffect(() => {
    if (expanded && refList.current && refListItem.current) {
      if (buttonRef.current && expanded) {
        buttonRef.current.focus();
      }
      const scrollHeightItem = refListItem.current.offsetTop;
      refList.current.scrollTo(0, scrollHeightItem - 5); //5px padding from top for shadowed gap
    }
  }, [expanded]);

  const handleKeyDown = (event: React.KeyboardEvent, index: number, isCurrent: boolean) => {

    if (!expanded) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (index < values.length - 1) {
          const nextButton = document.querySelector(
            `[data-select-id="${id}"][data-index="${index + 1}"]`
          ) as HTMLButtonElement;
          nextButton?.focus();
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (index > 0) {
          const prevButton = document.querySelector(
            `[data-select-id="${id}"][data-index="${index - 1}"]`
          ) as HTMLButtonElement;
          prevButton?.focus();
        }
        break;
      case 'Escape':
        onExpand(false);
        openSelectButtonRef.current?.focus();
        break;
      case 'Enter':
      case ' ':
        if (isCurrent) {
          event.preventDefault();
          onExpand(false);
          openSelectButtonRef.current?.focus();
        }
        break;

    }
  };


  const iconStyles = expanded ? { transform: 'rotate(180deg)' } : {};

  const listClasses = classNames(
    styles.list,
    expanded ? styles.visible : styles.hidden
  );

  const listStyles = {
    transition: `all ease-in-out ${ANIMATION_DURATION}ms`
  };

  // @todo labels on buttons

  return (
    <div className={styles.menu}>
      <button
        className={classNames(styles.block, styles.header, styles.rowItem, expanded ? styles.headerOpen : styles.headerClosed)}
        onClick={() => onExpand(!expanded)}
        aria-expanded={expanded}
        ref={openSelectButtonRef}
      >
        <span className={classNames(styles.block, styles.imageWrapper)}>
          <img src={currUrl} title={currLabel} alt={currLabel} />
        </span>
        <span className={classNames(styles.label, styles.headerLabel)}>
          <span className={classNames(styles.block, styles.textLeft)}
                dangerouslySetInnerHTML={{ __html: currLabel ?? '' }} />
          <span className={classNames(styles.block, styles.arrowIcon)}>
            <Icon aria-hidden="true" type={'arrow'} style={iconStyles} />
          </span>
        </span>
      </button>
      <ul tabIndex={-1} {...{ inert: !expanded ? '' : undefined }} ref={refList} className={listClasses}
          style={listStyles}>
        {values.map((v, index) => (
          <li
            key={index}
            ref={v === curr ? refListItem : undefined}
            // className={classNames(styles.rowItem, styles.listItem, v === curr && styles.selected)}
            // onClick={v === curr ? noop : () => clickHandler(v.id)}
          >
            <button
              onClick={v === curr ? noop : () => clickHandler(v.id)}
              aria-current={v === curr ? 'true' : undefined}
              ref={v === curr ? buttonRef : undefined}
              onKeyDown={(e) => handleKeyDown(e, index, v === curr)}
              data-index={index}
              data-select-id={id}  // neue data-attribute
              tabIndex={expanded ? 0 : -1}
              className={classNames(styles.block, styles.rowItem, styles.listItem, v === curr && styles.selected)}
            >
                <span aria-hidden="true" className={styles.imageWrapper}>
                  <img src={v.iconUrl} title={v.label} alt={v.label} />
                </span>
              <span className={styles.label}>
                  <span dangerouslySetInnerHTML={{ __html: v.label }} />
                </span>
            </button>
          </li>
        ))}
      </ul>
    </div>);
};

export default SelectInput;
